import AppLayout from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StrandBadge from "@/components/StrandBadge";
import ModeBadge from "@/components/ModeBadge";
import { statusLabels } from "@/components/StatusIndicator";
import { ArrowLeft, Clock, Play, CheckCircle, Filter, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAssessments, useCurrentUser, type AssessmentStatus } from "@/lib/api";
import StartSelfServeButton from "@/components/StartSelfServeButton";

interface Task {
  id: string;
  title: string;
  mode: "presentational" | "exploratory" | "collaborative";
  strand: "physical" | "linguistic" | "cognitive" | "social-emotional";
  dueDate: string;
  unit: string;
  status: string;
  timeLimit?: number;
}

export default function StudentTasksPage() {
  const { data: user } = useCurrentUser();
  const { data: assessments, isLoading } = useAssessments();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const getModeFromAssessmentMode = (mode: string): "presentational" | "exploratory" | "collaborative" => {
    if (mode === "presenting") return "presentational";
    if (mode === "explaining") return "exploratory";
    return "collaborative";
  };

  const getMainStrand = (mode: string) => {
    switch (mode) {
      case "presenting":
        return "physical";
      case "explaining":
        return "cognitive";
      case "persuading":
        return "social-emotional";
      default:
        return "physical";
    }
  };

const getStatusDisplay = (status: string) => {
    const label = statusLabels[status as AssessmentStatus] || status;
    const colorMap: Record<string, string> = {
      pending: "text-muted-foreground",
      uploading: "text-blue-600",
      processing: "text-amber-600",
      draft_ready: "text-green-600",
      under_review: "text-purple-600",
      signed_off: "text-emerald-600",
      error: "text-red-600",
    };
    const bgMap: Record<string, string> = {
      pending: "bg-secondary",
      uploading: "bg-blue-100 dark:bg-blue-900",
      processing: "bg-amber-100 dark:bg-amber-900",
      draft_ready: "bg-green-100 dark:bg-green-900",
      under_review: "bg-purple-100 dark:bg-purple-900",
      signed_off: "bg-emerald-100 dark:bg-emerald-900",
      error: "bg-red-100 dark:bg-red-900",
    };
    return { label, color: colorMap[status] || "text-muted-foreground", bg: bgMap[status] || "bg-secondary" };
  };

  const { tasks, stats, filteredTasks } = useMemo(() => {
    const defaultTasks: Task[] = [];
    const defaultStats = { total: 0, pending: 0, inProgress: 0, completed: 0, errored: 0 };

    if (!assessments || !Array.isArray(assessments)) {
      return { tasks: defaultTasks, stats: defaultStats, filteredTasks: defaultTasks };
    }

    const total = assessments.length;
    const completed = assessments.filter((a) => a.status === "signed_off").length;
    const pending = assessments.filter((a) => a.status === "pending").length;
    const inProgress = assessments.filter(
      (a) => a.status === "uploading" || a.status === "processing" || a.status === "draft_ready" || a.status === "under_review"
    ).length;
    const errored = assessments.filter((a) => a.status === "error").length;

    const allTasks: Task[] = assessments.map((a) => ({
      id: a.id,
      title: a.prompt_name || `${a.mode.charAt(0).toUpperCase() + a.mode.slice(1)} Assessment`,
      mode: getModeFromAssessmentMode(a.mode),
      strand: getMainStrand(a.mode),
      dueDate: a.created_at ? new Date(a.created_at).toLocaleDateString() : "Flexible",
      unit: a.cohort_name || "General",
      status: a.status,
      timeLimit: a.time_limit_seconds,
    }));

    return {
      tasks: allTasks,
      stats: { total, completed, pending, inProgress, errored },
      filteredTasks: allTasks,
    };
  }, [assessments]);

  const displayTasks = useMemo(() => {
    if (statusFilter === "all") return tasks;
    if (statusFilter === "pending") return tasks.filter((t) => t.status === "pending");
    if (statusFilter === "in_progress") return tasks.filter(
      (t) => t.status === "uploading" || t.status === "processing" || t.status === "draft_ready" || t.status === "under_review"
    );
    if (statusFilter === "completed") return tasks.filter((t) => t.status === "signed_off");
    return tasks;
  }, [tasks, statusFilter]);

  // Only truly fresh (pending) tasks go in "To Do" / link to the recorder. An
  // uploading task already has a recording in flight, so it belongs in progress
  // (links to feedback), not back on the recorder where a second recording would
  // be rejected by the backend.
  const pendingTasks = displayTasks.filter((t) => t.status === "pending");
  const inProgressTasks = displayTasks.filter(
    (t) => t.status === "uploading" || t.status === "processing" || t.status === "draft_ready" || t.status === "under_review"
  );
  const completedTasks = displayTasks.filter((t) => t.status === "signed_off");
  const errorTasks = displayTasks.filter((t) => t.status === "error");

  return (
    <AppLayout role="student">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Link
            to="/student"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl font-bold text-foreground mb-1">My Tasks</h1>
            <p className="text-muted-foreground">View and manage your oracy assessments</p>
          </motion.div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="py-3">
            <CardContent className="flex flex-col items-center gap-1 p-0">
              <div className="text-2xl font-bold text-primary">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total Tasks</p>
            </CardContent>
          </Card>
          <Card className="py-3">
            <CardContent className="flex flex-col items-center gap-1 p-0">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card className="py-3">
            <CardContent className="flex flex-col items-center gap-1 p-0">
              <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card className="py-3">
            <CardContent className="flex flex-col items-center gap-1 p-0">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="py-3">
            <CardContent className="flex flex-col items-center gap-1 p-0">
              <div className="text-2xl font-bold text-red-600">{stats.errored}</div>
              <p className="text-xs text-muted-foreground">Needs Attention</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading */}
        {isLoading ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Loading tasks...
            </CardContent>
          </Card>
        ) : displayTasks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <p className="font-medium text-foreground">All caught up!</p>
              <p className="text-sm text-muted-foreground mb-4">No tasks at the moment.</p>
              <StartSelfServeButton className="flex flex-col items-center" />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Pending Tasks */}
            {pendingTasks.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">To Do</h2>
                <div className="space-y-3">
                  {pendingTasks.map((task, index) => {
                    const statusInfo = getStatusDisplay(task.status);
                    return (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link to={`/student/task/${task.id}`}>
                          <Card className="hover:shadow-md transition-shadow">
                            <CardContent className="flex items-center gap-4 p-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${statusInfo.bg}`}>
                                <Clock className={`w-5 h-5 ${statusInfo.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm truncate">{task.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <ModeBadge mode={task.mode} />
                                  <StrandBadge strand={task.strand} />
                                  <span className={`text-xs ${statusInfo.color}`}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                              </div>
                              <Button size="sm" className="gap-1">
                                Start <Play className="w-3.5 h-3.5" />
                              </Button>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* In Progress Tasks */}
            {inProgressTasks.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">In Progress</h2>
                <div className="space-y-3">
                  {inProgressTasks.map((task, index) => {
                    const statusInfo = getStatusDisplay(task.status);
                    return (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link to={`/student/feedback/${task.id}`}>
                          <Card className="hover:shadow-md transition-shadow">
                            <CardContent className="flex items-center gap-4 p-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${statusInfo.bg}`}>
                                {(task.status === "uploading" || task.status === "processing") ? (
                                  <Loader2 className={`w-5 h-5 ${statusInfo.color} animate-spin`} />
                                ) : (
                                  <Play className={`w-5 h-5 ${statusInfo.color}`} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm truncate">{task.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <ModeBadge mode={task.mode} />
                                  <StrandBadge strand={task.strand} />
                                  <span className={`text-xs ${statusInfo.color}`}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                              </div>
                              <Button size="sm" variant="outline" className="gap-1">
                                View <FileText className="w-3.5 h-3.5" />
                              </Button>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Errored Tasks */}
            {errorTasks.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">Needs Attention</h2>
                <div className="space-y-3">
                  {errorTasks.map((task, index) => {
                    const statusInfo = getStatusDisplay(task.status);
                    return (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link to={`/student/feedback/${task.id}`}>
                          <Card className="hover:shadow-md transition-shadow">
                            <CardContent className="flex items-center gap-4 p-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${statusInfo.bg}`}>
                                <AlertCircle className={`w-5 h-5 ${statusInfo.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm truncate">{task.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <ModeBadge mode={task.mode} />
                                  <StrandBadge strand={task.strand} />
                                  <span className={`text-xs ${statusInfo.color}`}>
                                    Something went wrong. Please try again or contact your teacher.
                                  </span>
                                </div>
                              </div>
                              <Button size="sm" variant="outline" className="gap-1">
                                Try Again <Play className="w-3.5 h-3.5" />
                              </Button>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Completed Tasks */}
            {completedTasks.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-3">Completed</h2>
                <div className="space-y-3">
                  {completedTasks.map((task, index) => {
                    const statusInfo = getStatusDisplay(task.status);
                    return (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link to={`/student/feedback/${task.id}`}>
                          <Card className="hover:shadow-md transition-shadow">
                            <CardContent className="flex items-center gap-4 p-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${statusInfo.bg}`}>
                                <CheckCircle className={`w-5 h-5 ${statusInfo.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm truncate">{task.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <ModeBadge mode={task.mode} />
                                  <StrandBadge strand={task.strand} />
                                  <span className={`text-xs ${statusInfo.color}`}>
                                    {statusInfo.label}
                                  </span>
                                </div>
                              </div>
                              <Button size="sm" variant="outline" className="gap-1">
                                View Report <FileText className="w-3.5 h-3.5" />
                              </Button>
                            </CardContent>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
